import { useEffect, useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { useLazyQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { LOGIN_STEPS, OTP_EXPIRES_IN } from '../../constants/login';
import { LOGIN_USER } from '../../apis/user';
import { ONBOARDING } from '../../constants/routes';
import { REMOVE_UNDO_REDO_VERSION } from '../../apis/version';
import { useAuth } from '../../context/AuthContext';
import AuthUserTemplate from '../../components/templates/auth-user';
import InviteExpiredError from '../../domain/workspaces/login/invite-expired-error';
import InviteUserCompleteWorkspaceProfile from '../../domain/workspaces/login/complete-workspace-profile';
import LoginError from '../../domain/workspaces/login/login-error';
import LoginWithEmailOrSSO from '../../domain/workspaces/login/login-with-email-or-sso';
import Toaster from '../../components/Snackbar/Toaster';
import useTimer from '../../hooks/use-timer';
import useWorkspaceInvite from '../../context/WorkspaceInviteContext';
import VerifyOTPViaEmail from '../../domain/workspaces/login/verify-otp-via-email';

const LoginSteps = (props) => {
  // init
  const { email, setEmail, setIsLoading, setToast, step, setLoginStep, ssoLogin, formattedTimeLeft, timeLeft, isOtpExpired, startTimer, previousOtpEmail, setPreviousOtpEmail, onEmailLoginSuccess } = props;

  const handleDifferentEmail = () => {
    setLoginStep(LOGIN_STEPS.ENTER_EMAIL);
  };

  switch (step) {
    case LOGIN_STEPS.ENTER_EMAIL: return <LoginWithEmailOrSSO startTimer={startTimer} ssoLogin={ssoLogin} email={email} setEmail={setEmail} setIsLoading={setIsLoading} setToast={setToast} setLoginStep={setLoginStep} previousEmailOtp={previousOtpEmail} setPreviousEmailOtp={setPreviousOtpEmail} timeLeft={timeLeft} />;
    case LOGIN_STEPS.VERIFY_OTP: return <VerifyOTPViaEmail email={email} setIsLoading={setIsLoading} setToast={setToast} formattedTimeLeft={formattedTimeLeft} startTimer={startTimer} timeLeft={timeLeft} setLoginStep={setLoginStep} isOtpExpired={isOtpExpired} handleVerifyCodeSuccess={onEmailLoginSuccess} />;
    case LOGIN_STEPS.LOGIN_ERROR: return <LoginError linkText="Try a different email" onLinkClick={handleDifferentEmail} />;
    case LOGIN_STEPS.COMPLETE_PROFILE: return <InviteUserCompleteWorkspaceProfile setIsLoading={setIsLoading} setToast={setToast} />;
    case LOGIN_STEPS.INVITE_EXPIRED_ERROR: return <InviteExpiredError />;
    default: return <LoginWithEmailOrSSO startTimer={startTimer} ssoLogin={ssoLogin} email={email} setEmail={setEmail} setIsLoading={setIsLoading} setToast={setToast} setLoginStep={setLoginStep} previousEmailOtp={previousOtpEmail} setPreviousEmailOtp={setPreviousOtpEmail} timeLeft={timeLeft} />;
  }
};

const WorkspaceLogin = () => {
  // init
  const { isInviteUser, isInviteVerified, isInviteVerifying, email: userEmail } = useWorkspaceInvite();
  const { isLoggedIn, setIsLoggedIn } = useAuth();
  const navigate = useNavigate();

  // states
  const [loginStep, setLoginStep] = useState(undefined);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', autoHideDuration: 3000 });
  const [previousOtpEmail, setPreviousOtpEmail] = useState('');
  const [isOtpExpired, setIsOtpExpired] = useState(false);

  useEffect(() => {
    if (isInviteUser) {
      if (isInviteVerified === true) setLoginStep(isLoggedIn && userEmail ? LOGIN_STEPS.COMPLETE_PROFILE : LOGIN_STEPS.ENTER_EMAIL);
      else if (isInviteVerified === false) setLoginStep(LOGIN_STEPS.INVITE_EXPIRED_ERROR);
    } else setLoginStep(LOGIN_STEPS.ENTER_EMAIL);
  }, [isInviteVerified]);

  // constants
  const { formattedTime, timeLeft, startTimer } = useTimer({ expiresIn: OTP_EXPIRES_IN, setIsTerminated: setIsOtpExpired });

  // mutations / queries
  const [loginUser] = useLazyQuery(LOGIN_USER, { fetchPolicy: 'no-cache' });
  const [removeUndoRedoVersions] = useLazyQuery(REMOVE_UNDO_REDO_VERSION, { fetchPolicy: 'network-only' });

  // close the toast
  const handleLogoutSuccessClose = () => {
    setToast((prevstate) => ({ ...prevstate, open: false }));
  };

  // Email login using JWT
  const onEmailLoginSuccess = async () => {
    if (isInviteUser && isInviteVerified) {
      setLoginStep(LOGIN_STEPS.COMPLETE_PROFILE);
      return;
    }

    try {
      await removeUndoRedoVersions();
      setIsLoggedIn(true);
      navigate(ONBOARDING);
    } catch (error) {
      setToast((prevstate) => ({
        ...prevstate,
        message: 'Something went wrong!',
        open: true,
        type: 'error',
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // SSO using Google
  const onLoginSuccess = async (res) => {
    try {
      setIsLoading(true);
      // getting token from google api response using code.
      const loginData = await loginUser({ variables: { code: res.code } });
      await removeUndoRedoVersions();

      if (loginData.data && !loginData.loading && !loginData.error) {
        setIsLoggedIn(true);
        navigate(ONBOARDING);
      }
    } catch (err) {
      setToast((prevstate) => ({
        ...prevstate,
        message: 'Something went wrong!',
        open: true,
        type: 'error',
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const onLoginFailure = () => {
    <Toaster open onClose={handleLogoutSuccessClose} message="login failed" type="error" />;
  };

  const ssoLogin = useGoogleLogin({
    onSuccess: onLoginSuccess,
    onError: onLoginFailure,
    flow: 'auth-code',
  });

  return (
    <>
      <AuthUserTemplate loginStep={loginStep} isLoading={isLoading || isInviteVerifying} showTAndC={loginStep !== LOGIN_STEPS.LOGIN_ERROR}>
        {
          loginStep &&
          <LoginSteps
            ssoLogin={ssoLogin}
            email={email}
            previousOtpEmail={previousOtpEmail}
            setPreviousOtpEmail={setPreviousOtpEmail}
            setEmail={setEmail}
            setIsLoading={setIsLoading}
            setToast={setToast}
            step={loginStep}
            setLoginStep={setLoginStep}
            isOtpExpired={isOtpExpired}
            startTimer={startTimer}
            formattedTimeLeft={formattedTime}
            timeLeft={timeLeft}
            onEmailLoginSuccess={onEmailLoginSuccess}
          />
        }
      </AuthUserTemplate>
      <Toaster {...toast} onClose={handleLogoutSuccessClose} />
    </>
  );
};

LoginSteps.propTypes = {
  ssoLogin: PropTypes.func.isRequired,
  step: PropTypes.symbol.isRequired,
  email: PropTypes.string.isRequired,
  setEmail: PropTypes.func.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  setToast: PropTypes.func.isRequired,
  setLoginStep: PropTypes.func.isRequired,
  formattedTimeLeft: PropTypes.string.isRequired,
  startTimer: PropTypes.func.isRequired,
  timeLeft: PropTypes.number.isRequired,
  isOtpExpired: PropTypes.bool.isRequired,
  previousOtpEmail: PropTypes.string.isRequired,
  setPreviousOtpEmail: PropTypes.func.isRequired,
  onEmailLoginSuccess: PropTypes.func.isRequired,
};

export default WorkspaceLogin;
